<!-- FloatingMenu.vue -->
<template>
  <!-- Desktop Menu (hidden on mobile) -->
  <div class="floating-menu-card floating-menu-card--desktop">
    <div class="menu-header">
      <div class="menu-header-btn">Menu</div>
    </div>
    <div class="menu-navigation">
      <a
        v-for="item in menuItems"
        :key="item.url"
        :href="item.url"
        :class="[
          'menu-nav-item',
          { 'menu-nav-item--active': isActive(item) },
          { 'menu-nav-item--sub': item.isSub },
        ]"
      >
        {{ item.label }}
      </a>
    </div>
  </div>

  <!-- Mobile Menu Button (hidden on desktop) -->
  <button
    class="floating-menu-mobile-btn"
    @click="openBottomSheet"
    aria-label="Open menu"
  >
    <svg
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M15.6398 7.62146V9.41967H5V5.5354H13.5519C14.7052 5.5354 15.6398 6.47003 15.6398 7.62146Z"
        fill="#E00069"
      />
      <path
        d="M5 10.7063H15.6398V12.5045C15.6398 13.6578 14.7052 14.5924 13.5519 14.5924H7.08606C5.93281 14.5924 5 13.6578 5 12.5045V10.7063Z"
        fill="#E00069"
      />
      <path
        d="M7.73006 17.768C7.11062 17.7807 6.59685 17.2906 6.58227 16.6694C6.56952 16.0499 7.05961 15.5361 7.68087 15.5216C8.30032 15.5143 8.81227 16.0044 8.83049 16.6238C8.8396 17.2432 8.34769 17.7534 7.73006 17.7661"
        fill="#003D6A"
      />
      <path
        d="M11.8125 16.6388C11.8198 16.0211 12.3263 15.5256 12.9439 15.5311C13.5615 15.5383 14.0571 16.0448 14.0516 16.6624C14.0443 17.2801 13.5378 17.7756 12.9202 17.7702C12.3044 17.7665 11.8089 17.2655 11.8125 16.6497C11.8125 16.646 11.8125 16.6424 11.8125 16.6388Z"
        fill="#003D6A"
      />
      <path
        d="M13.5834 4.35221H7.05371V2.33538C7.05371 1.96736 7.3525 1.66675 7.72234 1.66675H12.9166C13.2846 1.66675 13.5852 1.96554 13.5852 2.33538V4.35221H13.5834Z"
        fill="#003D6A"
      />
    </svg>
  </button>

  <!-- Mobile Bottom Sheet Overlay -->
  <teleport to="body">
    <transition name="bottom-sheet-fade">
      <div
        v-if="isBottomSheetOpen"
        class="bottom-sheet-overlay"
        @click="closeBottomSheet"
      ></div>
    </transition>
    <transition name="bottom-sheet-slide">
      <div v-if="isBottomSheetOpen" class="bottom-sheet">
        <div class="bottom-sheet__header">
          <span class="bottom-sheet__title">Menu</span>
          <button
            class="bottom-sheet__close"
            @click="closeBottomSheet"
            aria-label="Close menu"
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M18 6L6 18"
                stroke="#3B4648"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path
                d="M6 6L18 18"
                stroke="#3B4648"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </button>
        </div>
        <div class="bottom-sheet__nav">
          <router-link
            v-for="item in menuItems"
            :key="item.url"
            :to="item.url"
            :class="[
              'bottom-sheet__nav-item',
              { 'bottom-sheet__nav-item--active': isActive(item) },
              { 'bottom-sheet__nav-item--sub': item.isSub },
            ]"
            @click="closeBottomSheet"
          >
            {{ item.label }}
          </router-link>
        </div>
      </div>
    </transition>
  </teleport>
</template>

<script setup>
import { ref } from "vue";
import { useRoute } from "vue-router";

const route = useRoute();
const currentUrl = ref(route.path);
const isBottomSheetOpen = ref(false);

const menuItems = [
  { url: "/makercart/guide/1", label: "Purposeful Maker Mindset" },
  { url: "/makercart/guide/2", label: "Maker Approach" },
  { url: "/makercart/guide/3", label: "About the Maker Cart" },
  { url: "/makercart/guide/4", label: "Instructional Material" },
  { url: "/makercart/guide/5", label: "Makercart Anatomy" },
  { url: "/makercart/guide/6", label: "Conducting a Maker Activity" },
  {
    url: "/makercart/guide/7",
    label: "Role 1: Designing Suitable Activities",
    isSub: true,
  },
  { url: "/makercart/guide/8", label: "Role 2: Posing Questions", isSub: true },
  {
    url: "/makercart/guide/9",
    label: "Role 3: Use Appropriate Language",
    isSub: true,
  },
  { url: "/makercart/guide/10", label: "Facilitating a Maker Activity" },
  { url: "/makercart/guide/11", label: "Material Management" },
];

const isActive = (item) => {
  if (item.url === "/makercart/guide/6") {
    return currentUrl.value.startsWith("/makercart/guide/6");
  }
  return currentUrl.value === item.url;
};

const openBottomSheet = () => {
  isBottomSheetOpen.value = true;
  document.body.style.overflow = "hidden";
};

const closeBottomSheet = () => {
  isBottomSheetOpen.value = false;
  document.body.style.overflow = "";
};
</script>

<style scoped>
/* ============ DESKTOP SIDEBAR ============ */
.floating-menu-card {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 246px;
  min-width: 246px;
  border-radius: 12px;
  box-shadow:
    0px 6px 10px 0px rgba(0, 0, 0, 0.15),
    0px 2px 3px 0px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  flex-shrink: 0;
}

.menu-header {
  background-color: var(--green-dark-1);
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 8px;
  width: 100%;
  border-radius: 12px 12px 0 0;
  box-sizing: border-box;
}

.menu-header-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  height: 48px;
  padding: 10px 16px;
  border-radius: 12px;
  width: 100%;
  box-sizing: border-box;
  font-family: "League Spartan", sans-serif;
  font-weight: 700;
  font-size: var(--fs-big);
  line-height: 24px;
  letter-spacing: 0.024px;
  color: #ffffff;
}

.menu-navigation {
  background-color: #ffffff;
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: center;
  justify-content: center;
  padding: 8px;
  width: 100%;
  border-radius: 0 0 12px 12px;
  box-sizing: border-box;
}

.menu-nav-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  border-radius: 12px;
  width: 100%;
  box-sizing: border-box;
  font-family: "League Spartan", sans-serif;
  font-weight: 700;
  font-size: var(--fs-big);
  line-height: 24px;
  letter-spacing: 0.024px;
  color: var(--green-dark-1);
  text-decoration: none;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.menu-nav-item:hover {
  background-color: #f0f9f5;
}

.menu-nav-item--active {
  background-color: #026843;
  color: #ffffff;
}

.menu-nav-item--active:hover {
  background-color: #026843;
}

.menu-nav-item--sub {
  padding-left: 32px;
  padding-right: 16px;
  height: auto;
  min-height: 48px;
}

/* ============ MOBILE MENU BUTTON ============ */
.floating-menu-mobile-btn {
  display: none;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border: 1px solid var(--primary-blue);
  border-radius: 8px;
  background: transparent;
  cursor: pointer;
  flex-shrink: 0;
  transition: background-color 0.2s ease;
}

.floating-menu-mobile-btn:hover {
  background-color: #f2f3fb;
}

/* ============ BOTTOM SHEET ============ */
.bottom-sheet-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 999;
}

.bottom-sheet {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  background: #ffffff;
  border-radius: 28px 28px 0 0;
  box-shadow:
    0px 4px 8px 3px rgba(0, 0, 0, 0.15),
    0px 1px 3px 0px rgba(0, 0, 0, 0.1);
  z-index: 1000;
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 0 16px 24px 16px;
  max-height: 80vh;
  overflow-y: auto;
}

.bottom-sheet__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 0;
  border-bottom: 1px solid #d0d5dd;
  position: sticky;
  top: 0;
  background: #ffffff;
  z-index: 1;
}

.bottom-sheet__title {
  font-family: "League Spartan", sans-serif;
  font-weight: 500;
  font-size: var(--fs-large);
  line-height: 28px;
  color: var(--primary-blue);
}

.bottom-sheet__close {
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  width: 24px;
  height: 24px;
}

.bottom-sheet__nav {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 8px;
  background: #ffffff;
  border-radius: 0 0 12px 12px;
}

.bottom-sheet__nav-item {
  display: flex;
  align-items: center;
  gap: 8px;
  height: 48px;
  padding: 10px 16px;
  border-radius: 12px;
  width: 100%;
  box-sizing: border-box;
  font-family: "League Spartan", sans-serif;
  font-weight: 700;
  font-size: var(--fs-medium);
  line-height: 24px;
  letter-spacing: 0.024px;
  color: var(--green-dark-1);
  text-decoration: none;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.bottom-sheet__nav-item:hover {
  background-color: #f0f9f5;
}

.bottom-sheet__nav-item--active {
  background-color: #026843;
  color: #ffffff;
}

.bottom-sheet__nav-item--active:hover {
  background-color: #026843;
}

.bottom-sheet__nav-item--sub {
  padding-left: 32px;
  padding-right: 16px;
  height: auto;
  min-height: 48px;
}

/* ============ TRANSITIONS ============ */
.bottom-sheet-fade-enter-active,
.bottom-sheet-fade-leave-active {
  transition: opacity 0.3s ease;
}

.bottom-sheet-fade-enter-from,
.bottom-sheet-fade-leave-to {
  opacity: 0;
}

.bottom-sheet-slide-enter-active,
.bottom-sheet-slide-leave-active {
  transition: transform 0.3s ease;
}

.bottom-sheet-slide-enter-from,
.bottom-sheet-slide-leave-to {
  transform: translateY(100%);
}

/* ============ RESPONSIVE ============ */
@media (max-width: 768px) {
  .floating-menu-card--desktop {
    display: none;
  }

  .floating-menu-mobile-btn {
    display: flex;
  }
}
</style>
